import { get } from 'lodash';
import { CERTIFICATES } from '../../certificate/certificates';
import { Medicine } from '../../../interfaces/medicine';
import { PrescriberConfiguration } from '../../../shared/prescriber';

/**
 * Camada 2 do Epic MEVO-5349 (alerta de numeração SNCR). Módulo puro (sem
 * dependência de React) — consumido pelo componente de alerta
 * (`SncrFallbackAlert`, Task 4), pela finalização em `PrescriptionEmission.tsx`
 * e pela obrigatoriedade de certificado digital no `prescriptionStore`. Nunca
 * bloqueia a emissão em si (RN-01): no cenário vermelho, só restringe o método
 * de finalização ao manual (`shouldRestrictFinalizationToManual`).
 *
 * Importante (RN-06 / separação de camadas — ver Epic MEVO-5349): nada aqui lê
 * status de certificado/certificadora/assinatura (`CERTIFIER_STATUS`,
 * `isCertifierUnstable`, `ownedCertificates`, `selectedCertificateType`). A
 * Camada 1 (bloqueio de certificadora `INACTIVE`/`PARTIALLY_ACTIVE`, em
 * `PrescriptionEmission.tsx`) roda inteiramente à parte e não é tocada por este
 * módulo.
 */

export type SncrAlertResult = 'nenhum' | 'amarelo' | 'vermelho';

type SncrAlertReason = 'notificacaoSemNumeracao' | 'brancaSemNumeracaoForaDaJanela';

export interface SncrAlertDisplay {
  variant: SncrAlertResult;
  reason?: SncrAlertReason;
}

/**
 * Textos dos dois cenários vermelhos, consumidos exclusivamente pelo box de alerta
 * (`SncrFallbackAlert`, Task 4). Não há toast associado a este texto — o cenário vermelho
 * nunca bloqueia a finalização com um aviso bloqueante, só restringe o método
 * (`shouldRestrictFinalizationToManual`).
 */
const SNCR_RED_MESSAGE =
  'Você não possui assinatura eletrônica e/ou\nnumerações do SNCR. A receita precisa ser\nimpressa, carimbada e assinada manualmente.';

export const SNCR_ALERT_RED_MESSAGES: Record<SncrAlertReason, string> = {
  notificacaoSemNumeracao: SNCR_RED_MESSAGE,
  brancaSemNumeracaoForaDaJanela: SNCR_RED_MESSAGE,
};

const BRANCAS_TYPES = ['RCE', 'RET'];

/**
 * Data de corte da janela de transição regulatória (Art. 18 da RDC 1.000/2025).
 * Valor efetivo em produção: **29/10/2026** — amarelo até 29/10 inclusive,
 * vermelho a partir de 30/10 (confirmado pelo dev; diverge do PRD escrito do
 * PPB-14, que diz 30/10 — ver Epic MEVO-5349 para a nota de rastreabilidade,
 * este valor é o correto).
 *
 * Sobrescrevível via `Features.sncrTransitionWindowEnd` (mesmo mecanismo de
 * configuração por prescritor já usado por `Features.enableSNCR`), permitindo
 * ajustar/desativar a janela sem novo deploy (Task 3).
 */
export const DEFAULT_SNCR_TRANSITION_WINDOW_END = '2026-10-29T23:59:59-03:00';

/**
 * `Features.enableSNCR` — mesma flag de negócio por prescritor já usada em outros pontos do
 * repo (ex. `PrescriptionContent.tsx`), reaproveitada como flag de rollout deste Epic (Task 8)
 * por decisão do dev — não há uma flag técnica separada. Com ela desligada, nada deste módulo
 * se aplica: sem alerta, sem restrição de método e sem exceção à obrigatoriedade do certificado
 * digital — rollback integral.
 */
export function isSncrFallbackFlowEnabled(
  prescriberConfiguration: PrescriberConfiguration | null | undefined
): boolean {
  return !!get(prescriberConfiguration, 'Features.enableSNCR', false);
}

export function getTransitionWindowEnd(prescriberConfiguration: PrescriberConfiguration | null | undefined): Date {
  const configured = get(
    prescriberConfiguration,
    'Features.sncrTransitionWindowEnd',
    DEFAULT_SNCR_TRANSITION_WINDOW_END
  );
  const parsed = new Date(configured);

  return Number.isNaN(parsed.getTime()) ? new Date(DEFAULT_SNCR_TRANSITION_WINDOW_END) : parsed;
}

const missingNumbering = (item: Medicine) => !!item.NumeroSncrRequerido && !item.NumeroSncrReservado;
const requiresNumbering = (item: Medicine) => !!item.NumeroSncrRequerido;

function computeSncrAlertDisplay(
  items: Medicine[] | undefined,
  prescriberConfiguration: PrescriberConfiguration | null | undefined,
  referenceDate: Date,
  selectedCertificateType?: string | null
): SncrAlertDisplay {
  if (!isSncrFallbackFlowEnabled(prescriberConfiguration)) return { variant: 'nenhum' };

  const safeItems = items ?? [];
  const brancasItems = safeItems.filter((item) => !item.TipoSncr || BRANCAS_TYPES.includes(item.TipoSncr));
  const notificacoesItems = safeItems.filter((item) => item.TipoSncr && !BRANCAS_TYPES.includes(item.TipoSncr));

  const isManualSelected = selectedCertificateType === CERTIFICATES.MANUAL;

  const hasNotificacaoSemNumeracao = notificacoesItems.some(missingNumbering);

  if (hasNotificacaoSemNumeracao) return { variant: 'vermelho', reason: 'notificacaoSemNumeracao' };

  // Quando o profissional escolhe assinar manualmente, qualquer item que exija numeração do
  // SNCR — branca ou notificação, com ou sem saldo/numeração já reservada — precisa forçar o
  // alerta vermelho, sobrescrevendo o alerta amarelo padrão das "brancas" (RCE/RET) e o caso
  // "nenhum" das notificações que já têm saldo.
  if (isManualSelected) {
    if (notificacoesItems.some(requiresNumbering)) return { variant: 'vermelho', reason: 'notificacaoSemNumeracao' };
    if (brancasItems.some(requiresNumbering))
      return { variant: 'vermelho', reason: 'brancaSemNumeracaoForaDaJanela' };
  }

  // Fora da seleção manual, o alerta das "brancas" (RCE/RET) só aparece quando falta
  // saldo/numeração reservada — com saldo, não há necessidade de avisar.
  const hasBrancaSemNumeracao = brancasItems.some(missingNumbering);

  if (hasBrancaSemNumeracao) {
    const dentroDaJanela = referenceDate <= getTransitionWindowEnd(prescriberConfiguration);
    if (dentroDaJanela) return { variant: 'amarelo' };
    return { variant: 'vermelho', reason: 'brancaSemNumeracaoForaDaJanela' };
  }

  return { variant: 'nenhum' };
}

/**
 * Calcula o resultado do alerta SNCR (Task 2): `nenhum` / `amarelo` / `vermelho`.
 *
 * Gate de entrada: `Features.enableSNCR` desligada retorna sempre `'nenhum'`,
 * antes de qualquer outro cálculo (critério de aceite da Task 2).
 */
export function getSncrAlertResult(
  items: Medicine[] | undefined,
  prescriberConfiguration: PrescriberConfiguration | null | undefined,
  referenceDate: Date = new Date(),
  selectedCertificateType?: string | null
): SncrAlertResult {
  return computeSncrAlertDisplay(items, prescriberConfiguration, referenceDate, selectedCertificateType).variant;
}

/**
 * Mesma computação de `getSncrAlertResult`, mas incluindo o motivo do cenário
 * vermelho (notificação sem numeração vs. branca fora da janela) — usado só
 * pelo componente de alerta (Task 4) para escolher o texto correto, sem
 * reimplementar a lógica de cálculo.
 */
export function getSncrAlertDisplay(
  items: Medicine[] | undefined,
  prescriberConfiguration: PrescriberConfiguration | null | undefined,
  referenceDate: Date = new Date(),
  selectedCertificateType?: string | null
): SncrAlertDisplay {
  return computeSncrAlertDisplay(items, prescriberConfiguration, referenceDate, selectedCertificateType);
}

/**
 * Indica se o alerta SNCR está no cenário `vermelho` (Task 8 — gate de rollback):
 * mesmo com certificado digital selecionado, a falta de assinatura eletrônica
 * e/ou numeração do SNCR força a finalização manual da receita.
 */
export function shouldRestrictFinalizationToManual(
  items: Medicine[] | undefined,
  prescriberConfiguration: PrescriberConfiguration | null | undefined,
  referenceDate: Date = new Date()
): boolean {
  return getSncrAlertResult(items, prescriberConfiguration, referenceDate) === 'vermelho';
}
